import { useKpisPerimeter, type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { searchStores } from '#domain/core/filter/filter.query';
import { isNielsenInterval, PERIOD_TO_LOOKER_PERIOD, type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

interface UseStoreDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useStoreData({ debouncedSearch, enabled, checkedItemsState, taxonomy }: UseStoreDataProps) {
  const {
    period: { selectedPeriod },
    storePerimeter: { storePerimeter }
  } = useKpisPerimeter();

  const currentStoreFilters = computed(() => {
    return Object.fromEntries(
      Object.entries(checkedItemsState.value.storesFilters).filter(([, state]) => state.state === 'checked')
    );
  });

  const {
    data: storeData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [searchStores.name, debouncedSearch, taxonomy, selectedPeriod, storePerimeter],
    queryFn: async () => {
      const periodField = PERIOD_TO_LOOKER_PERIOD[selectedPeriod.value.period];
      const unrefTaxonomy = unref(taxonomy);

      if (
        unrefTaxonomy !== 'nielsen' &&
        periodField !== 'period_code' &&
        !isNielsenInterval(selectedPeriod.value.periodInterval) &&
        storePerimeter.value === 'likeForLike'
      ) {
        return searchStores({
          search: debouncedSearch.value,
          orNodeIdIn: debouncedSearch.value ? Object.keys(currentStoreFilters.value) : undefined,
          limit: 100,
          taxonomy: unrefTaxonomy,
          storePerimeter: unref(storePerimeter),
          period: {
            field: periodField,
            value: selectedPeriod.value.periodInterval
          }
        });
      }

      return searchStores({
        search: debouncedSearch.value,
        orNodeIdIn: debouncedSearch.value ? Object.keys(currentStoreFilters.value) : undefined,
        limit: 100,
        taxonomy: unref(taxonomy),
        storePerimeter: 'allStores'
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsStores = computed(() => {
    if (!storeData.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: storeData.value,
      defaultFilters: storeData.value,
      selectedFilters: currentStoreFilters.value
    });
  });

  const isLoadingStores = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return {
    isLoadingStores,
    itemsStores
  };
}
